import { HiOutlineSparkles } from "react-icons/hi";
import {
  MODULE_META,
  childMetaLabelFromSchema,
  getChildBrief,
  getChildSummary,
  isSchemaAvailable,
  sortSchemaEntries,
} from "pages/ServiceProvider/SpSetting/AiSettings/aiSettingsConfig";

const coerceBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const moduleHasAvailableChildren = (moduleMeta) =>
  Object.values(moduleMeta?.children || {}).some((childMeta) =>
    isSchemaAvailable(childMeta)
  );

/** Enablement modal card copy (design: designs/ai-enablement.pen). Falls back to MODULE_META.description. */
const ENABLEMENT_MODULE_DESCRIPTION = {
  ai_care_watch:
    "Monitors shifts and flags potential risks such as unusual hours, missing breaks or early finishes. AI provides insights and does not replace a fresh review of the information.",
  ai_copilot:
    "Answers questions in plain language about participants, workers, rosters, funding and incidents. It never updates payroll or participant records.",
};

/**
 * Modules and sub-features enabled for this org, limited to API schema entries
 * marked available (shipped). New shipped features appear after backend deploy
 * without changing this modal.
 *
 * @param {Record<string, { label?: string, available?: boolean, children?: Record<string, { label?: string, available?: boolean }> }>|null|undefined} schema
 * @param {Record<string, Record<string, unknown>>|null|undefined} aiFeatures
 * @returns {Array<{ key: string, name: string, description: string, Icon: import("react").ComponentType<{ className?: string }>, items: Array<{ title: string, detail: string }> }>}
 */
export const buildAiEnablementFeatureList = (schema, aiFeatures) => {
  if (!schema || typeof schema !== "object") {
    return [];
  }

  const nodes = aiFeatures && typeof aiFeatures === "object" ? aiFeatures : {};
  const list = [];

  sortSchemaEntries(Object.entries(schema)).forEach(([moduleKey, moduleMeta]) => {
    if (!isSchemaAvailable(moduleMeta)) {
      return;
    }

    const node = nodes[moduleKey] || {};
    if (!coerceBool(node.enabled)) {
      return;
    }

    const ui = MODULE_META[moduleKey] || {};
    const items = [];

    Object.entries(moduleMeta.children || {}).forEach(([childKey, childMeta]) => {
      if (!isSchemaAvailable(childMeta)) {
        return;
      }
      if (!coerceBool(node[childKey])) {
        return;
      }
      const title = childMetaLabelFromSchema(childMeta);
      const brief = getChildBrief(moduleKey, childKey, title);
      items.push({
        title,
        detail: getChildSummary(moduleKey, childKey, brief),
      });
    });

    if (moduleHasAvailableChildren(moduleMeta) && items.length === 0) {
      return;
    }

    list.push({
      key: moduleKey,
      name: ui.displayName || moduleMeta.label || moduleKey,
      description: ENABLEMENT_MODULE_DESCRIPTION[moduleKey] || ui.description || "",
      Icon: ui.icon || HiOutlineSparkles,
      items,
    });
  });

  return list;
};
